import {
	CheckOutlined,
	DeleteOutlined,
	LockFilled,
	ReloadOutlined,
} from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { Form } from "components/Form/Form"
import { Input, type InputRef } from "components/Input/Input"
import { Disclosure } from "components/Disclosure/Disclosure"
import { Empty } from "components/Empty/Empty"
import { NoDataIcon } from "icons/NoDataIcon"
import { WifiIcon } from "icons/WifiIcon"
import { useRef } from "react"
import { cn } from "styles/cn"
import { Feedback } from "./Feedback"
import { useFootprint } from "./useFootprint"

export function SelectWifi() {
	const footprint = useFootprint()
	const wifiPasswordInput = useRef<InputRef>(null)
	const wifiList = footprint.wifiList ?? []
	const spinning =
		!!footprint.executing.WIFILIST ||
		!!footprint.executing.WIFICONN ||
		!!footprint.executing.WIFIDELETE
	const connectedWiFiSignal = wifiList.find(
		({ ssid }) => footprint.wifiSsid === ssid,
	)?.signal

	return (
		<Feedback spinning={spinning}>
			<div className="flex flex-col gap-2 rounded-2xl bg-mint-0 p-6">
				<h3 className="font-nunito text-2xl font-semibold text-mint-100">
					Configure device's network
				</h3>

				<div className="flex flex-col gap-4">
					{footprint.isWifiConnected && (
						<div className="flex flex-col gap-2">
							<div className="flex items-center justify-between">
								<p className="text-navy-60">My network</p>

								<Button
									danger
									icon={<DeleteOutlined aria-hidden />}
									onClick={async () => footprint.deleteWifi()}
								>
									Delete
								</Button>
							</div>
							<div className="flex w-full items-center justify-between gap-4 rounded-xl border border-green-60 bg-white p-3">
								<CheckOutlined aria-hidden className="text-green-60" />
								<p className="mr-auto truncate text-lg font-semibold">
									{footprint.wifiSsid}
								</p>
								<div className="flex gap-2">
									<LockFilled aria-hidden className="text-xl" />
									<WifiIcon aria-hidden signal={connectedWiFiSignal} />
								</div>
							</div>
						</div>
					)}

					{footprint.wifiSsid && !footprint.isWifiConnected && (
						<div className="flex flex-col">
							<Form.Item
								layout="vertical"
								label={`Enter the password for "${footprint.wifiSsid}" (leave blank if open network)`}
								className="[&_.ant-form-item-label]:font-semibold"
								{...(footprint.hasError
									? {
											hasFeedback: true,
											validateStatus: "error",
											help: footprint.message,
										}
									: {})}
							>
								<Input.Password
									ref={wifiPasswordInput}
									aria-label="Wi-Fi password"
									autoFocus
									autoComplete="off"
									value={footprint.wifiPassword}
									onChange={({ target }) =>
										footprint.setWifiPassword(target.value)
									}
								/>
							</Form.Item>
							<Button
								type="primary"
								className="w-full"
								disabled={footprint.isWifiConnected}
								onClick={async () => await footprint.connectToWifi()}
							>
								Connect to Wi-Fi network
							</Button>
						</div>
					)}

					<Disclosure
						open={!footprint.isWifiConnected}
						responsive={false}
						className="bg-white"
						summary={
							<div className="flex w-full items-center justify-between">
								<p className="text-navy-60">
									{footprint.isWifiConnected ? "Other" : "All"} networks
								</p>

								<Button
									type="link"
									icon={<ReloadOutlined aria-hidden />}
									disabled={!footprint.serialNumber}
									onClick={async () => await footprint.getWifiList()}
								>
									{wifiList.length > 0 ? "Refresh" : "Load list"}
								</Button>
							</div>
						}
					>
						<ul
							className="flex flex-col gap-2"
							aria-label={`${wifiList.length} Wi-Fi networks available`}
						>
							{wifiList.length === 0 && <Empty Icon={NoDataIcon} />}

							{wifiList
								.filter(({ ssid }) =>
									footprint.isWifiConnected
										? ssid !== footprint.wifiSsid
										: true,
								)
								.toSorted((a, b) => b.signal - a.signal)
								.map(({ ssid, signal }) => {
									const ariaLabel = `Wi-Fi network "${ssid}" with signal strength of ${signal}`

									return (
										<li
											key={ssid}
											tabIndex={0}
											aria-label={ariaLabel}
											className={cn(
												"focus-outline flex w-full items-center justify-between gap-4 rounded-xl border border-mint-20 bg-white p-3 pl-9 hover:cursor-pointer hover:border-green-60",
												ssid === footprint.wifiSsid && "bg-mint-20",
											)}
											onClick={() => {
												footprint.setWifiSsid(
													footprint.wifiSsid === ssid ? undefined : ssid,
												)
												wifiPasswordInput.current?.focus()
											}}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													footprint.setWifiSsid(
														footprint.wifiSsid === ssid ? undefined : ssid,
													)
													wifiPasswordInput.current?.focus()
												}
											}}
										>
											<p className="truncate text-lg font-semibold">{ssid}</p>
											<div className="flex gap-2">
												<LockFilled aria-hidden className="text-xl" />
												<WifiIcon aria-hidden signal={signal} />
											</div>
										</li>
									)
								})}
						</ul>
					</Disclosure>
				</div>
			</div>
		</Feedback>
	)
}
